/**
 * MODULE: domain/pages/frontend/components/editor/PageLinkPickerModal.tsx
 * PURPOSE: Choose a workspace page to insert as a page_link block
 */

import { useMemo, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, TextInput } from 'flowbite-react';
import { HiOutlineDatabase, HiOutlineDocumentText, HiOutlineSearch } from 'react-icons/hi';
import type { PageTreeNode } from '../../../contracts/pages.types';
import { usePageTree } from '../../hooks/usePages';
import { flattenPageTree } from '../../utils/pageTreeUtils';

type Props = {
  open: boolean;
  workspaceId: string;
  currentPageId: string;
  onClose: () => void;
  onSelect: (page: { id: string; title: string; icon: string | null; page_type: 'note' | 'database' }) => void;
};

function matchesQuery(page: PageTreeNode, query: string): boolean {
  const haystack = `${page.title} ${page.page_type}`.toLowerCase();
  return haystack.includes(query);
}

function filterTree(nodes: PageTreeNode[], query: string): PageTreeNode[] {
  const normalized = query.trim().toLowerCase();
  const pages = flattenPageTree(nodes).map((item) => item.node);
  if (!normalized) return pages;
  return pages.filter((page) => matchesQuery(page, normalized));
}

export function PageLinkPickerModal({
  open,
  workspaceId,
  currentPageId,
  onClose,
  onSelect,
}: Props) {
  const [query, setQuery] = useState('');
  const { data: tree = [], isLoading, isError } = usePageTree(workspaceId);

  const candidates = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return filterTree(tree, normalized).filter((page) => page.id !== currentPageId);
  }, [currentPageId, query, tree]);

  const handleClose = () => {
    setQuery('');
    onClose();
  };

  return (
    <Modal show={open} onClose={handleClose} size="md">
      <ModalHeader>Link to page</ModalHeader>
      <ModalBody data-testid="pages-page-link-picker">
        <TextInput
          icon={HiOutlineSearch}
          sizing="sm"
          placeholder="Search pages…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Search pages to link"
          data-testid="pages-page-link-search"
        />
        <div className="mt-3 max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size="sm" aria-label="Loading pages" />
            </div>
          ) : null}
          {isError ? (
            <p className="px-3 py-4 text-sm text-red-600">Could not load pages.</p>
          ) : null}
          {!isLoading && !isError && candidates.length === 0 ? (
            <p className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">No pages found.</p>
          ) : null}
          {!isLoading && !isError
            ? candidates.map((page) => {
                const TypeIcon = page.page_type === 'database' ? HiOutlineDatabase : HiOutlineDocumentText;
                return (
                  <button
                    key={page.id}
                    type="button"
                    className="flex w-full items-center gap-2 border-b border-gray-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800"
                    onClick={() => {
                      onSelect({
                        id: page.id,
                        title: page.title || 'Untitled',
                        icon: page.icon,
                        page_type: page.page_type,
                      });
                      handleClose();
                    }}
                    data-testid={`pages-page-link-option-${page.id}`}
                  >
                    {page.icon ? (
                      <span className="text-base" aria-hidden>
                        {page.icon}
                      </span>
                    ) : (
                      <TypeIcon className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
                    )}
                    <span className="truncate font-medium text-gray-900 dark:text-white">
                      {page.title || 'Untitled'}
                    </span>
                  </button>
                );
              })
            : null}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={handleClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}
